import { Injectable, inject, computed, resource } from "@angular/core";
import { IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import type { IFirmUserRole } from "./ifirm-user-roles-models";
import type { IiFirmUserRolesMap } from "@interfaces/index";

@Injectable({ providedIn: "root" })
export class IFirmUserRolesService {
    private iframeMessagesService = inject(IframeMessagesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private iFirmUserRolesLoadPromise?: Promise<IFirmUserRole[]>;
    iFirmUserRoles: IFirmUserRole[] = [];

    private iFirmUserRolesResource = resource<IFirmUserRole[] | undefined, undefined>({
        defaultValue: undefined,
        loader: () => this.getiFirmUserRoles(),
    });
    public sIFirmUserRoles = computed<IFirmUserRole[] | undefined>(() => this.iFirmUserRolesResource.value());

    readonly roleUserIds = computed<IiFirmUserRolesMap>(() => {
        const items = this.sIFirmUserRoles();
        if (!items || !items.length) return;

        const map: IiFirmUserRolesMap = new Map();
        for (const ur of items) {
            const arr = map.get(ur.RoleId);
            if (arr) arr.push(ur);
            else map.set(ur.RoleId, [ur]);
        }
        return map;
    });

    async loadiFirmUserRoles() {
        try {
            const result = await this.iframeMessagesService.postMessageResp("getiFirmUserRoles", {}, "gotiFirmUserRoles");
            if (result.status === "success") {
                this.iFirmUserRoles = this.translateiFirmUserRoles(result.userRoles);
                return this.iFirmUserRoles;
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
        return [];
    }

    async getiFirmUserRoles() {
        if (!this.iFirmUserRolesLoadPromise) {
            this.iFirmUserRolesLoadPromise = this.loadiFirmUserRoles();
        }
        return this.iFirmUserRolesLoadPromise;
    }

    translateiFirmUserRoles(iFirmUserRoles: IFirmUserRole[]): IFirmUserRole[] {
        return iFirmUserRoles.map((iFirmUserRole) => {
            return {
                UserId: `${iFirmUserRole.UserId}`,
                RoleId: `${iFirmUserRole.RoleId}`,
            };
        });
    }

    private userRolesCache: { [key: string]: IFirmUserRole[] } = {};
    getRolesByUser(userId: string): IFirmUserRole[] {
        if (this.userRolesCache[userId]) {
            return this.userRolesCache[userId];
        }
        const userRoles = this.iFirmUserRoles.filter((role) => role.UserId === userId);
        this.userRolesCache[userId] = userRoles;
        return userRoles;
    }
}
